import Link from 'next/link'

export const COURSES_FAQ_SECTIONS = [
  {
    title: 'LEARNING & OUTCOMES',
    items: [
      {
        question: 'What are Codingal courses and what does my child learn?',
        answerText:
          'Codingal courses help kids and teens learn by building real projects such as websites, apps, games, animations, and interactive creations. Learners can also build strong math foundations through guided practice. Families can choose long-term learning paths, short camps, or specialized courses based on interest, including scratch coding for kids.',
        answer: (
          <>
            Codingal courses help kids and teens learn by building real projects
            such as websites, apps, games, animations, and interactive
            creations. Learners can also build strong math foundations through
            guided practice. Families can choose long-term learning paths, short
            camps, or specialized courses based on interest, including{' '}
            <Link href="/courses/scratch-programming/">
              <a
                className="text-blue-200 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                scratch coding for kids
              </a>
            </Link>
            .
          </>
        ),
      },
      {
        question: 'What are the benefits of learning coding for my child?',
        answerText:
          'Learning to code improves logical thinking, critical thinking, creativity, math, and writing skills. Codingal classes build confidence through small wins and hands-on projects, and students can reinforce learning using coding quizzes for kids.',
        answer: (
          <>
            Learning to code improves logical thinking, critical thinking,
            creativity, math, and writing skills. Codingal classes build
            confidence through small wins and hands-on projects, and students
            can reinforce learning using{' '}
            <Link href="/quizzes/">
              <a
                className="text-blue-200 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                coding quizzes for kids
              </a>
            </Link>
            .
          </>
        ),
      },
      {
        question:
          'What college and career opportunities can Codingal courses lead to?',
        answerText:
          'Coding builds future-ready skills that apply across industries. Students develop logic, reasoning, and computational thinking for careers like software development, game development for kids, and data science.',
        answer: (
          <>
            Coding builds future-ready skills that apply across industries.
            Students develop logic, reasoning, and computational thinking for
            careers like software development,{' '}
            <Link href="/courses/game-development/">
              <a
                className="text-blue-200 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                game development for kids
              </a>
            </Link>
            , and data science.
          </>
        ),
      },
    ],
  },
  {
    title: 'COURSE FORMATS & EXPERIENCE',
    items: [
      {
        question:
          'What is the difference between full courses, camps, private 1:1 lessons, and group lessons?',
        answerText:
          'Full courses are structured learning journeys. Codingal also offers coding camps for kids with different themes such as summer coding camps for kids, winter coding camps for kids, and more. These fun, outcome-focused camps typically last a week and include 20+ engaging camp options. Private 1:1 lessons provide personalized live instruction, while group lessons encourage collaborative and peer-based learning.',
        answer: (
          <>
            Full courses are structured learning journeys. Codingal also offers
            coding camps for kids with different themes such as{' '}
            <Link href="/kids-teens-coding-camps/summer/">
              <a
                className="text-blue-200 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                summer coding camps for kids
              </a>
            </Link>
            ,{' '}
            <Link href="/kids-teens-coding-camps/winter/">
              <a
                className="text-blue-200 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                winter coding camps for kids
              </a>
            </Link>
            , and more. These fun, outcome-focused camps typically last a week
            and include 20+ engaging camp options. Private 1:1 lessons provide
            personalized live instruction, while group lessons encourage
            collaborative and peer-based learning.
          </>
        ),
      },
      {
        question: 'What is the online private 1:1 class experience like?',
        answerText:
          'In a private 1:1 class, your child gets undivided attention from an experienced instructor who tailors each lesson to your child&apos;s unique learning style and goals. The curriculum is personalized to focus on the areas your child wants to explore, whether it&apos;s mastering coding concepts, building projects, or working on specific challenges. Classes are scheduled at your convenience, allowing flexibility to fit into your child&apos;s routine. During each lesson, your child actively participates in hands-on learning, guided by the instructor&apos;s real-time feedback and encouragement. Progress is tracked through the personalized dashboard, where both you and your child can see how they are progressing, access class recordings, and review past lessons. The instructor also provides continuous support and adjusts the pace based on your child&apos;s individual needs, ensuring they remain motivated and challenged without feeling overwhelmed.',
        answer: (
          <>
            In a private 1:1 class, your child gets undivided attention from an
            experienced instructor who tailors each lesson to your child&apos;s
            unique learning style and goals. The curriculum is personalized to
            focus on the areas your child wants to explore, whether it&apos;s
            mastering coding concepts, building projects, or working on specific
            challenges. Classes are scheduled at your convenience, allowing
            flexibility to fit into your child&apos;s routine. During each
            lesson, your child actively participates in hands-on learning,
            guided by the instructor&apos;s real-time feedback and
            encouragement. Progress is tracked through the personalized
            dashboard, where both you and your child can see how they are
            progressing, access class recordings, and review past lessons. The
            instructor also provides continuous support and adjusts the pace
            based on your child&apos;s individual needs, ensuring they remain
            motivated and challenged without feeling overwhelmed.
          </>
        ),
      },
      {
        question: 'Are Codingal courses good for beginners?',
        answerText:
          'Yes. Beginners start with block-based coding and gradually move to text-based programming with guidance and projects, often beginning with scratch programming for kids.',
        answer: (
          <>
            Yes. Beginners start with block-based coding and gradually move to
            text-based programming with guidance and projects, often beginning
            with{' '}
            <Link href="/courses/scratch-programming/">
              <a
                className="text-blue-200 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                scratch programming for kids
              </a>
            </Link>
            .
          </>
        ),
      },
    ],
  },
  {
    title: 'TRIAL, PRICING & SCHEDULING',
    items: [
      {
        question: 'Do you offer a free live trial lesson?',
        answerText:
          'Yes. You can start with a free live trial lesson to experience Codingal classes.',
        answer: (
          <>
            Yes. You can start with a{' '}
            <Link href="/register/?utm_medium=course-listing-page">
              <a
                className="text-blue-200 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                free live trial lesson
              </a>
            </Link>{' '}
            to experience Codingal classes.
          </>
        ),
      },
      {
        question: 'Do you offer scholarships?',
        answerText:
          'Scholarships are discussed after trial completion with your Academic Counselor and reflected in the pricing section.',
        answer: (
          <>
            Scholarships are discussed after trial completion with your Academic
            Counselor and reflected in the{' '}
            <a href="#pricing" className="text-blue-200 hover:underline">
              pricing
            </a>{' '}
            section.
          </>
        ),
      },
      {
        question: 'How does pricing work and where can I see fees?',
        answerText:
          'Pricing depends on the course and learning format. You can view pricing in the  section and explore flexible payment options after the trial.',
        answer: (
          <>
            Pricing depends on the course and learning format. You can view
            pricing in the{' '}
            <a href="#pricing" className="text-blue-200 hover:underline">
              Codingal pricing
            </a>{' '}
            section and explore flexible payment options after the trial.
          </>
        ),
      },
    ],
  },
  {
    title: 'PRACTICE, CERTIFICATES & PROGRESS',
    items: [
      {
        question: 'Will my child receive homework?',
        answerText:
          'Projects and quizzes are included by default, and extra practice can be shared if requested. Students can also practice using coding quizzes for kids.',
        answer: (
          <>
            Projects and quizzes are included by default, and extra practice can
            be shared if requested. Students can also practice using{' '}
            <Link href="/quizzes/">
              <a
                className="text-blue-200 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                coding quizzes for kids
              </a>
            </Link>
            .
          </>
        ),
      },
      {
        question: 'Will my child get certificates?',
        answerText:
          'Yes. Trial and course completion certificates are provided, including coding certificate for kids with STEM.org accreditation.',
        answer: (
          <>
            Yes. Trial and course completion certificates are provided,
            including coding certificate for kids with STEM.org accreditation.
          </>
        ),
      },
      {
        question: 'Will we get class recordings and progress updates?',
        answerText:
          'Yes. Recordings, progress reports, and monthly parent–teacher meetings are available via the dashboard.',
        answer: (
          <>
            Yes. Recordings, progress reports, and monthly parent–teacher
            meetings are available via the dashboard.
          </>
        ),
      },
    ],
  },
  {
    title: 'TEACHERS, SAFETY & TRUST',
    items: [
      {
        question: 'How do you select and train teachers?',
        answerText:
          'Teachers are graduate IT professionals selected through screening, teaching demos, background checks, and ongoing training. Learn more about Codingal teachers.',
        answer: (
          <>
            Teachers are graduate IT professionals selected through screening,
            teaching demos, background checks, and ongoing training. Learn more
            about{' '}
            <Link href="/teach/">
              <a
                className="text-blue-200 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Codingal teachers
              </a>
            </Link>
            .
          </>
        ),
      },
      {
        question: 'Is Codingal safe and legit for kids?',
        answerText:
          'Codingal is trusted globally, ranked an After-School Program on Trustpilot, and follows child-safe learning practices with secure recordings. Read Trustpilot reviews and parent testimonials.',
        answer: (
          <>
            Codingal is trusted globally, ranked an After-School Program on
            Trustpilot, and follows child-safe learning practices with secure
            recordings. Read{' '}
            <Link href="https://www.trustpilot.com/review/www.codingal.com">
              <a
                className="text-blue-200 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Trustpilot reviews
              </a>
            </Link>{' '}
            and{' '}
            <Link href="/reviews-and-testimonials/">
              <a
                className="text-blue-200 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                parent testimonials
              </a>
            </Link>
            .
          </>
        ),
      },
    ],
  },
  {
    title: 'TECH & ACCESS',
    items: [
      {
        question: 'What device does my child need?',
        answerText:
          'A laptop or desktop with internet, webcam, and microphone is recommended.',
        answer: (
          <>
            A laptop or desktop with internet, webcam, and microphone is
            recommended.
          </>
        ),
      },
      {
        question: 'How do I log in or join a Codingal class?',
        answerText:
          'After enrollment, access the dashboard to join classes, view recordings, reschedule sessions, track progress, and contact support.',
        answer: (
          <>
            After enrollment, access the dashboard to join classes, view
            recordings, reschedule sessions, track progress, and contact
            support.
          </>
        ),
      },
    ],
  },
]
